import { Link } from "react-router-dom";
import { Phone, MessageSquareText, ClipboardList, Wrench, Trash2, Leaf, Home as HomeIcon, HeartHandshake, BadgePercent } from "lucide-react";
import { useSite } from "../../lib/site";
import { track } from "../../lib/api";

export const SERVICE_CATS = [
  { key: "handyman", title: "Handyman Help", icon: Wrench, blurb: "Small household tasks within our confirmed scope, done carefully and cleaned up after.", to: "/services#handyman" },
  { key: "junk", title: "Junk Removal", icon: Trash2, blurb: "Furniture, mattresses, disconnected appliances, garage clutter and more — loaded and hauled.", to: "/services#junk" },
  { key: "yard", title: "Yard Cleanup", icon: Leaf, blurb: "Leaves, weeds and nonhazardous yard debris cleared and hauled away.", to: "/services#yard" },
  { key: "cleanout", title: "Property & Rental Cleanouts", icon: HomeIcon, blurb: "Move-out, pre-sale, estate and rental cleanouts with before-and-after photos.", to: "/services#cleanout" },
  { key: "senior", title: "Senior Home Support", icon: HeartHandshake, blurb: "Patient, respectful help with decluttering, downsizing and small approved tasks.", to: "/senior-home-support" },
];

export function CallButton({ className = "btn-red", label, testid = "call-btn" }) {
  const { tel, phone } = useSite();
  return (
    <a href={tel} className={className} onClick={() => track("call_click")} data-testid={testid}>
      <Phone className="h-5 w-5" aria-hidden="true" /> {label || `Call ${phone}`}
    </a>
  );
}

export function TextButton({ className = "btn-outline", label = "Text Photos", testid = "text-btn" }) {
  const { sms } = useSite();
  return (
    <a href={sms} className={className} onClick={() => track("sms_click")} data-testid={testid}>
      <MessageSquareText className="h-5 w-5" aria-hidden="true" /> {label}
    </a>
  );
}

export function QuoteButton({ className = "btn-red", label = "Get My Free Estimate", testid = "quote-btn" }) {
  return (
    <Link to="/quote" className={className} onClick={() => track("quote_cta_click")} data-testid={testid}>
      <ClipboardList className="h-5 w-5" aria-hidden="true" /> {label}
    </Link>
  );
}

const SCOPE = { labor: "eligible labor", subtotal: "the eligible subtotal" };
const CAT_NAMES = { materials: "materials", disposal: "disposal fees", rental: "equipment rentals", permit: "permits", third_party: "third-party charges", labor: "labor", other: "other charges" };

export function SeniorTerms({ compact = false }) {
  const { site } = useSite();
  const s = site.senior;
  if (!s) return null;
  if (!s.approved) {
    return (
      <p className={compact ? "text-sm text-neutral-700" : "text-base text-neutral-700"} data-testid="senior-terms-pending">
        Senior discount details — eligibility age, which charges qualify, and any exclusions — are being finalized. Your written estimate will show exactly what the discount applies to before you approve anything. This is a lifetime percentage discount, not a price freeze.
      </p>
    );
  }
  const excl = (s.excluded_categories || []).map((c) => CAT_NAMES[c] || c).join(", ");
  return (
    <div className={compact ? "space-y-1 text-sm text-neutral-700" : "space-y-2 text-base text-neutral-800"} data-testid="senior-terms-approved">
      <p><strong>{s.percent}% off {SCOPE[s.scope] || "eligible charges"}</strong> for customers age {s.eligibility_age}+.{excl && ` Does not apply to ${excl}.`}</p>
      <p>{s.stacking_allowed ? "May be combined with other promotions." : "Cannot be combined with other promotions."} {s.minimum_treatment === "min_after_discount" ? "Our minimum charge still applies after the discount." : "The discount applies after our minimum charge."}</p>
      {!compact && <p>{s.enrollment_terms}</p>}
      <p>{s.lifetime_terms}</p>
      {!compact && s.extra_terms && <p>{s.extra_terms}</p>}
    </div>
  );
}

export function SeniorBanner() {
  return (
    <section className="bg-[#D9232E] text-white" data-testid="senior-banner">
      <div className="wrap flex flex-col gap-6 py-10 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <BadgePercent className="mt-1 h-10 w-10 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-display text-4xl font-extrabold uppercase leading-none sm:text-5xl">Seniors save 20% for life</p>
            <p className="mt-2 max-w-2xl text-lg">On eligible services, every visit — no subscription, no sign-up fee. See exactly what qualifies on your written estimate.</p>
          </div>
        </div>
        <Link to="/senior-home-support" className="btn bg-white text-[#171717] hover:bg-neutral-100" data-testid="senior-banner-link">How the senior discount works</Link>
      </div>
    </section>
  );
}

export function FinalCta({ title = "Tell us what you need. Get clear pricing. Let One Punch knock it out." }) {
  return (
    <section className="bg-[#171717] text-white" data-testid="final-cta">
      <div className="wrap py-16 sm:py-20">
        <h2 className="max-w-3xl text-4xl font-extrabold uppercase leading-tight sm:text-5xl">{title}</h2>
        <p className="mt-4 max-w-2xl text-lg text-neutral-300">Free estimates. Photos help, but they're never required — you can always just call.</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <QuoteButton testid="final-cta-quote-btn" />
          <CallButton className="btn-ghost-light" testid="final-cta-call-btn" />
        </div>
      </div>
    </section>
  );
}

export function PageHero({ eyebrow, title, children }) {
  return (
    <section className="border-b border-neutral-200 bg-[#F5F5F5]">
      <div className="wrap py-12 sm:py-16 fade-up">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="mt-2 max-w-4xl text-4xl font-extrabold uppercase leading-[1.05] sm:text-5xl lg:text-6xl">{title}</h1>
        {children && <div className="mt-4 max-w-3xl text-lg leading-relaxed text-neutral-700">{children}</div>}
      </div>
    </section>
  );
}
